import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

// Auth pages
import SplashScreen  from '@/routes/Auth/SplashScreen'
import LoginPage     from '@/routes/Auth/LoginPage'
import MFAPage       from '@/routes/Auth/MFAPage'
import SSOCallbackPage from '@/routes/Auth/SSOCallbackPage'

// Shell layout
import Shell from '@/components/layout/Shell'

// App pages
import DashboardPage   from '@/routes/DashboardPage'
const ActionCenterPage = lazy(() => import('@/routes/ActionCenterPage'))
import PortfolioPage   from '@/routes/PortfolioPage'
import AgentsPage      from '@/routes/AgentsPage'
import RiskPage        from '@/routes/RiskPage'
import EncryptionPage  from '@/routes/EncryptionPage'
import SettingsPage    from '@/routes/SettingsPage'
import TradesPage      from '@/routes/TradesPage'
import ChatPage        from '@/routes/ChatPage'
import AnalyticsPage   from '@/routes/AnalyticsPage'
import TerminalPage    from '@/routes/Terminal'
import WorkspacePage   from '@/routes/WorkspacePage'
import StandalonePanel from '@/routes/StandalonePanel'
import AdvisoryPage    from '@/routes/Advisory'
import AccountsPage         from '@/routes/AccountsPage'
import RecommendationsPage  from '@/routes/RecommendationsPage'
import AltDataPage           from '@/routes/AltDataPage'
const CongressPage = lazy(() => import('@/routes/CongressPage'))
const SignalsPage = lazy(() => import('@/routes/SignalsPage'))
const GlobalMapPage = lazy(() => import('@/routes/GlobalMapPage'))
const RegimePage = lazy(() => import('@/routes/RegimePage'))
const BlotterPage = lazy(() => import('@/routes/BlotterPage'))
import BacktestPage          from '@/routes/BacktestPage'
import WatchlistPage         from '@/routes/WatchlistPage'
import CalendarPage          from '@/routes/CalendarPage'
import BrainPage             from '@/routes/BrainPage'
import HeatmapPage           from '@/routes/HeatmapPage'
import ScreenerPage          from '@/routes/ScreenerPage'
import ChartPage             from '@/routes/ChartPage'
import StockDetailPage       from '@/routes/StockDetailPage'
import IndicesPage           from '@/routes/IndicesPage'
import IndexTriggerPage      from '@/routes/IndexTriggerPage'
import PersonasPage          from '@/routes/PersonasPage'
import NewsPage              from '@/routes/NewsPage'
import HelpPage              from '@/routes/HelpPage'
const SystemMapPage = lazy(() => import('@/routes/SystemMapPage'))
import SectorPage            from '@/routes/SectorPage'
import ReportsPage           from '@/routes/ReportsPage'
import JournalPage           from '@/routes/JournalPage'
import CryptoPage            from '@/routes/CryptoPage'
import ForexPage             from '@/routes/ForexPage'
import CommoditiesPage       from '@/routes/CommoditiesPage'
import EarningsPage          from '@/routes/EarningsPage'
import MacroPage             from '@/routes/MacroPage'
import OptimizerPage         from '@/routes/OptimizerPage'
import OnboardingPage        from '@/routes/OnboardingPage'
import StrategyLibraryPage   from '@/routes/StrategyLibraryPage'
import BacklogPage           from '@/routes/BacklogPage'
import IPOPage               from '@/routes/IPOPage'
import OptionsPage           from '@/routes/OptionsPage'
import DividendsPage         from '@/routes/DividendsPage'
import IncomePage            from '@/routes/IncomePage'
import IncomeProjectionPage  from '@/routes/IncomeProjectionPage'
import MonthlyIncomePage     from '@/routes/MonthlyIncomePage'
import CashPage              from '@/routes/CashPage'
import PlanningPage          from '@/routes/PlanningPage'
import MonteCarloPage        from '@/routes/MonteCarloPage'
import MonteCarloJurisdictionsPage from '@/routes/MonteCarloJurisdictionsPage'
import TaxPage               from '@/routes/TaxPage'
import PropertyPage          from '@/routes/PropertyPage'
import ConcentrationPage     from '@/routes/ConcentrationPage'
import IhtPage               from '@/routes/IhtPage'
import IhtMitigationPage     from '@/routes/IhtMitigationPage'
import PersonaRoundTablePage from '@/routes/PersonaRoundTablePage'
import PensionPage           from '@/routes/PensionPage'
import NetWorthPage          from '@/routes/NetWorthPage'
import DrawdownPage          from '@/routes/DrawdownPage'
import JurisdictionsPage     from '@/routes/JurisdictionsPage'
import AlertsPage            from '@/routes/AlertsPage'
import RebalancerPage        from '@/routes/RebalancerPage'
import ScenariosPage         from '@/routes/ScenariosPage'
import RelocationPlanPage    from '@/routes/RelocationPlanPage'
import ModelPortfoliosPage   from '@/routes/ModelPortfoliosPage'
import TaxLossPage           from '@/routes/TaxLossPage'
import CorrelationPage       from '@/routes/CorrelationPage'
import MessagingPage         from '@/routes/MessagingPage'
import DbPensionsPage        from '@/routes/DbPensionsPage'
import ScenarioStackerPage  from '@/routes/ScenarioStackerPage'
import HouseholdProfilePage  from '@/routes/HouseholdProfilePage'
import WaitlistAdminPage    from '@/routes/WaitlistAdminPage'
import ChangePasswordPage   from '@/routes/ChangePasswordPage'
import AuditLogPage         from '@/routes/AuditLogPage'
import LifeGoalsPage        from '@/routes/LifeGoalsPage'
import AdvisorPage          from '@/routes/AdvisorPage'
import IfaMeetingPage       from '@/routes/IfaMeetingPage'
import FamilyDashboardPage  from '@/routes/FamilyDashboardPage'
import AlphaEnginePage      from '@/routes/AlphaEnginePage'
import RebalanceQueuePage   from '@/routes/RebalanceQueuePage'
import SnapshotsPage        from '@/routes/SnapshotsPage'
const HypeCyclePage = lazy(() => import('@/routes/HypeCyclePage'))
const SmartMoneyPage = lazy(() => import('@/routes/SmartMoneyPage'))
const VelocityPage = lazy(() => import('@/routes/VelocityPage'))
const PhDSwarmPage = lazy(() => import('@/routes/PhDSwarmPage'))
const QuantResearchPage = lazy(() => import('@/routes/QuantResearchPage'))
const AIConnectorsPage = lazy(() => import('@/routes/AIConnectorsPage'))
const BrokerKeysPage = lazy(() => import('@/routes/BrokerKeysPage'))
import RequireCapability from '@/components/RequireCapability'
const TradeIdeasPage = lazy(() => import('@/routes/TradeIdeasPage'))
const HiddenAlphaPage = lazy(() => import('@/routes/HiddenAlphaPage'))
const CatalystsPage = lazy(() => import('@/routes/CatalystsPage'))
const FeedInventoryPage = lazy(() => import('@/routes/FeedInventoryPage'))
const SignalSearchPage = lazy(() => import('@/routes/SignalSearchPage'))
const SignalHeatmapPage = lazy(() => import('@/routes/SignalHeatmapPage'))
const SignalWebPage = lazy(() => import('@/routes/SignalWebPage'))
const DeployCashPage = lazy(() => import('@/routes/DeployCashPage'))
const SignalPopulationPage = lazy(() => import('@/routes/SignalPopulationPage'))
const PredictionsPage = lazy(() => import('@/routes/PredictionsPage'))
const ApiKeysPage = lazy(() => import('@/routes/ApiKeysPage'))
const TrendRadarPage = lazy(() => import('@/routes/TrendRadarPage'))
const ChartAnalyserPage = lazy(() => import('@/routes/ChartAnalyserPage'))
const TodaysReadPage = lazy(() => import('@/routes/TodaysReadPage'))
const VolatilityPage = lazy(() => import('@/routes/VolatilityPage'))
const PredictionMarketsPage = lazy(() => import('@/routes/PredictionMarketsPage'))
const ResearchPapersPage = lazy(() => import('@/routes/ResearchPapersPage'))
const GuidePage = lazy(() => import('@/routes/GuidePage'))
const TodayPage = lazy(() => import('@/routes/TodayPage'))
const RiskRadarPage = lazy(() => import('@/routes/RiskRadarPage'))
const SignalQualityPage = lazy(() => import('@/routes/SignalQualityPage'))
const QuiverStrategiesPage = lazy(() => import('@/routes/QuiverStrategiesPage'))
const BacktesterPage = lazy(() => import('@/routes/BacktesterPage'))
const AnomalyPage = lazy(() => import('@/routes/AnomalyPage'))
const AlphaLabPage = lazy(() => import('@/routes/AlphaLabPage'))
const PaperLabPage = lazy(() => import('@/routes/PaperLabPage'))
const CompetitionPage = lazy(() => import('@/routes/CompetitionPage'))
const PublicCompetitionPage = lazy(() => import('@/routes/PublicCompetitionPage'))
const PublicTerminalPage = lazy(() => import('@/routes/PublicTerminalPage'))
const LabTerminalPage = lazy(() => import('@/routes/LabTerminalPage'))
const CompetitionTestPage = lazy(() => import('@/routes/CompetitionTestPage'))
import TechnicalScannerPage from '@/routes/TechnicalScannerPage'
import NotificationsPage    from '@/routes/NotificationsPage'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center">
        <span className="sr-only">Loading…</span>
        <div className="w-8 h-8 border-2 border-blue/30 border-t-blue rounded-full animate-spin" aria-hidden />
      </div>
    )
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route index element={<SplashScreen />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/mfa"   element={<MFAPage />} />
        <Route path="/auth/sso-callback" element={<SSOCallbackPage />} />
        {/* The hackathon demo URLs — read-only contest book, no login.
            Data comes from one unauthenticated endpoint serving the
            contest account only. These lazy routes sit OUTSIDE the
            Shell (whose Suspense covers the /app pages), so they need
            their own boundary — without it they rendered BLANK. */}
        <Route path="/public/competition" element={
          <Suspense fallback={<div className="min-h-screen bg-black" />}>
            <PublicCompetitionPage />
          </Suspense>} />
        {/* The dense terminal view — the demo video's hero shot, live. */}
        <Route path="/public/terminal" element={
          <Suspense fallback={<div className="min-h-screen bg-black" />}>
            <PublicTerminalPage />
          </Suspense>} />

        {/* Onboarding (protected, no shell) */}
        <Route
          path="/app/onboarding"
          element={
            <RequireAuth>
              <OnboardingPage />
            </RequireAuth>
          }
        />

        {/* Standalone panel pop-out (protected, no shell/sidebar) */}
        <Route
          path="/app/workspace/panel"
          element={
            <RequireAuth>
              <StandalonePanel />
            </RequireAuth>
          }
        />

        {/* Protected shell */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          {/* The INDEX is the calm page. The overwhelmed household member
              lands where the platform answers "what should I do today?" —
              not on the 5,000-line pro dashboard with 44 jargon terms. The
              Command Centre keeps its power at /app/command-centre, one
              click away, and every old bookmark of it still works. */}
          <Route index                  element={<TodayPage />} />
          <Route path="command-centre"  element={<DashboardPage />} />
          <Route path="actions"         element={<ActionCenterPage />} />
          <Route path="accounts"        element={<AccountsPage />} />
          {/* Merged: flat holdings view now lives as a tab on Portfolio */}
          <Route path="holdings"        element={<Navigate to="/app/portfolio?tab=holdings" replace />} />
          <Route path="portfolio"       element={<PortfolioPage />} />
          {/* Merged: look-through is now a tab on Analytics */}
          <Route path="look-through"    element={<Navigate to="/app/analytics?tab=look-through" replace />} />
          {/* Merged: RRG is now a tab on Sectors */}
          <Route path="rrg"             element={<Navigate to="/app/sectors?tab=rrg" replace />} />
          {/* Merged: duplicate optimiser removed — /app/optimizer is canonical */}
          <Route path="optimiser"       element={<Navigate to="/app/optimizer" replace />} />
          <Route path="sectors"        element={<SectorPage />} />
          <Route path="agents"          element={<AgentsPage />} />
          <Route path="markets"         element={<Navigate to="/app/indices" replace />} />
          <Route path="crypto"          element={<CryptoPage />} />
          <Route path="forex"           element={<ForexPage />} />
          <Route path="commodities"     element={<CommoditiesPage />} />
          <Route path="news"            element={<NewsPage />} />
          {/* Merged: Monte Carlo goal projections now live as a tab on Life Goals */}
          <Route path="goals"           element={<Navigate to="/app/life-goals?tab=projections" replace />} />
          <Route path="risk"            element={<RiskPage />} />
          <Route path="encryption"      element={<EncryptionPage />} />
          <Route path="trades"          element={<TradesPage />} />
          <Route path="journal"         element={<JournalPage />} />
          <Route path="chat"            element={<ChatPage />} />
          <Route path="analytics"       element={<AnalyticsPage />} />
          <Route path="terminal"        element={<TerminalPage />} />
          <Route path="workspace"       element={<WorkspacePage />} />
          <Route path="advisory"        element={<AdvisoryPage />} />
          <Route path="recommendations" element={<RecommendationsPage />} />
          <Route path="intelligence"     element={<AltDataPage />} />
          <Route path="signals"          element={<SignalsPage />} />
          <Route path="congress"         element={<CongressPage />} />
          <Route path="regime"           element={<RegimePage />} />
          <Route path="blotter"          element={<BlotterPage />} />
          <Route path="map"              element={<GlobalMapPage />} />
          <Route path="backtest"        element={<BacktestPage />} />
          <Route path="strategies"     element={<StrategyLibraryPage />} />
          <Route path="watchlist"      element={<WatchlistPage />} />
          <Route path="calendar"      element={<CalendarPage />} />
          <Route path="earnings"     element={<EarningsPage />} />
          <Route path="brain"         element={<BrainPage />} />
          <Route path="heatmap"       element={<HeatmapPage />} />
          <Route path="screener"      element={<ScreenerPage />} />
          {/* /app/today stays alive for muscle memory and old links. */}
          <Route path="today"         element={<Navigate to="/app" replace />} />
          <Route path="chart"         element={<ChartPage />} />
          <Route path="options"       element={<OptionsPage />} />
          <Route path="dividends"     element={<DividendsPage />} />
          <Route path="income"        element={<IncomePage />} />
          <Route path="income-projection" element={<IncomeProjectionPage />} />
          <Route path="monthly-income"    element={<MonthlyIncomePage />} />
          <Route path="cash"          element={<CashPage />} />
          <Route path="planning"      element={<PlanningPage />} />
          <Route path="monte-carlo"   element={<RequireCapability capability="scenarios"><MonteCarloPage /></RequireCapability>} />
          <Route path="mc-jurisdictions" element={<MonteCarloJurisdictionsPage />} />
          <Route path="tax"           element={<TaxPage />} />
          <Route path="property"      element={<PropertyPage />} />
          <Route path="concentration" element={<ConcentrationPage />} />
          <Route path="iht"             element={<IhtPage />} />
          <Route path="iht-mitigation"  element={<IhtMitigationPage />} />
          <Route path="round-table"     element={<PersonaRoundTablePage />} />
          <Route path="pension"       element={<PensionPage />} />
          <Route path="net-worth"     element={<NetWorthPage />} />
          <Route path="drawdown"      element={<DrawdownPage />} />
          <Route path="jurisdictions" element={<JurisdictionsPage />} />
          <Route path="alerts"        element={<AlertsPage />} />
          <Route path="rebalancer"    element={<RequireCapability capability="auto_execute"><RebalancerPage /></RequireCapability>} />
          {/* Old server-emitted links used /app/rebalance — keep it working
              instead of falling through the catch-all to the splash screen. */}
          <Route path="rebalance"     element={<Navigate to="/app/rebalancer" replace />} />
          <Route path="alpha-engine"  element={<RequireCapability capability="auto_execute"><AlphaEnginePage /></RequireCapability>} />
          <Route path="rebalance-queue" element={<RequireCapability capability="auto_execute"><RebalanceQueuePage /></RequireCapability>} />
          <Route path="snapshots"    element={<SnapshotsPage />} />
          <Route path="scenarios"     element={<ScenariosPage />} />
          <Route path="stacker"       element={<ScenarioStackerPage />} />
          <Route path="relocation"    element={<RelocationPlanPage />} />
          <Route path="models"        element={<ModelPortfoliosPage />} />
          <Route path="tax-loss"      element={<TaxLossPage />} />
          <Route path="stock/:ticker" element={<StockDetailPage />} />
          <Route path="indices"       element={<IndicesPage />} />
          <Route path="indices/:index" element={<IndexTriggerPage />} />
          <Route path="personas"      element={<PersonasPage />} />
          <Route path="reports"       element={<ReportsPage />} />
          <Route path="macro"        element={<MacroPage />} />
          {/* Merged: Economic Index is now a tab on Macro */}
          <Route path="economic-index" element={<Navigate to="/app/macro?tab=economic-index" replace />} />
          <Route path="optimizer"    element={<OptimizerPage />} />
          <Route path="backlog"       element={<BacklogPage />} />
          <Route path="ipo"           element={<IPOPage />} />
          <Route path="db-pensions"   element={<DbPensionsPage />} />
          <Route path="profile"       element={<HouseholdProfilePage />} />
          <Route path="correlation"   element={<CorrelationPage />} />
          <Route path="messages"      element={<MessagingPage />} />
          <Route path="help"          element={<HelpPage />} />
          <Route path="system-map"    element={<SystemMapPage />} />
          <Route path="settings"        element={<SettingsPage />} />
          <Route path="admin/waitlist"  element={<WaitlistAdminPage />} />
          <Route path="admin/audit"     element={<AuditLogPage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="life-goals"    element={<LifeGoalsPage />} />
          <Route path="advisor"       element={<AdvisorPage />} />
          <Route path="ifa-meeting"   element={<IfaMeetingPage />} />
          <Route path="family"        element={<FamilyDashboardPage />} />
          <Route path="hype-cycle"   element={<HypeCyclePage />} />
          <Route path="smart-money" element={<SmartMoneyPage />} />
          <Route path="velocity"    element={<VelocityPage />} />
          <Route path="phd-swarm"  element={<PhDSwarmPage />} />
          <Route path="quant-research" element={<QuantResearchPage />} />
          <Route path="ai-connectors" element={<AIConnectorsPage />} />
          <Route path="broker-keys" element={<BrokerKeysPage />} />
          <Route path="trade-ideas" element={<TradeIdeasPage />} />
          {/* Merged: Alpha Brief is now a tab on Today's Read (/app/daily) */}
          <Route path="alpha-brief" element={<Navigate to="/app/daily?tab=alpha-brief" replace />} />
          <Route path="alpha-lab" element={<AlphaLabPage />} />
          <Route path="paper-lab" element={<PaperLabPage />} />
          <Route path="lab-terminal" element={<LabTerminalPage />} />
          <Route path="competition" element={<CompetitionPage />} />
          <Route path="competition-test" element={<CompetitionTestPage />} />
          <Route path="hidden-alpha" element={<HiddenAlphaPage />} />
          <Route path="catalysts" element={<CatalystsPage />} />
          <Route path="feeds" element={<FeedInventoryPage />} />
          <Route path="signal-search" element={<SignalSearchPage />} />
          <Route path="signal-heatmap" element={<SignalHeatmapPage />} />
          <Route path="signal-web" element={<SignalWebPage />} />
          <Route path="deploy-cash" element={<DeployCashPage />} />
          <Route path="signal-population" element={<SignalPopulationPage />} />
          <Route path="predictions" element={<PredictionsPage />} />
          <Route path="trend-radar" element={<TrendRadarPage />} />
          <Route path="chart-analyser" element={<ChartAnalyserPage />} />
          <Route path="daily" element={<TodaysReadPage />} />
          <Route path="prediction-markets" element={<PredictionMarketsPage />} />
          <Route path="volatility" element={<VolatilityPage />} />
          <Route path="research-papers" element={<ResearchPapersPage />} />
          <Route path="guide" element={<GuidePage />} />
          <Route path="api-keys" element={<ApiKeysPage />} />
          {/* Merged: Market Pulse is now a tab on Today's Read (/app/daily) */}
          <Route path="pulse" element={<Navigate to="/app/daily?tab=pulse" replace />} />
          <Route path="risk-radar" element={<RiskRadarPage />} />
          <Route path="signal-quality" element={<SignalQualityPage />} />
          <Route path="quiver-strategies" element={<QuiverStrategiesPage />} />
          <Route path="backtester" element={<BacktesterPage />} />
          <Route path="anomalies"  element={<AnomalyPage />} />
          {/* Merged: Correlation Engine is now a tab on Correlation */}
          <Route path="correlation-engine" element={<Navigate to="/app/correlation?tab=engine" replace />} />
          <Route path="technical-scanner" element={<TechnicalScannerPage />} />
          <Route path="notifications"     element={<NotificationsPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
